import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import { useSaveProviderNoteMutation } from "app/store/transmission/api/transmissionNotesApi";

import {
  onSpeechRecognizing,
  toggleSubmitting,
  onChangeProviderNotes,
  setProviderNotesTouched,
  selectedReport,
} from "app/store/transmission/slice/transmissionSlice";
import { showErrorToast, showSuccessToast } from "utils/toast";
import useDebounce from "app/hooks/useDebounce";
import { SUBMIT_ACTIONS } from "static/constants";
import useNotesFooter from "../footer/useNotesFooter";
import { useSignReportsMutation } from "app/store/transmission/api/transmissionTableApi";
import useTranscription from "hooks/useTranscription";

const useProviderNotes = (data) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const {
    reportCurrentTab,
    providerNotesExpanded,
    providerNoteSubmitting,
    providerNoteSubmittingAndProcessing,
    providerNoteSubmittingAndSigningAndProcessing,
    providerNoteSubmittingAndSigningAndUnProcessing,

    providerNotes,
    providerNotesTouched,
    originalProviderNotes,
    reportFIN,
    providerNotesEsigDisabled,
    reportDetail,
    isSpeechRecognizing,
    isLegacyReport,
    alertStatus,
    attentionStatus,
  } = useSelector((state) => state.transmission);

  const { accountsMapper } = useSelector((state) => state.account);

  const { handleProcess } = useNotesFooter(data);
  const [signReports] = useSignReportsMutation();

  const providerNotesRef = useRef(providerNotes);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (!hasInitialized) {
      const note = data?.provider_note?.note || "";
      dispatch(
        onChangeProviderNotes({
          note: note.trim(),
          notesTouched: false,
          isOriginal: true,
        })
      );
      setHasInitialized(true);
    }
  }, [data?.provider_note, dispatch, hasInitialized]);

  useEffect(() => {
    if (providerNotes) {
      const inputId = document.getElementById("provider-note");
      if (inputId && isSpeechRecognizing) {
        inputId.focus();
        inputId.setSelectionRange(providerNotes.length, providerNotes.length);
        inputId.scrollLeft = inputId.scrollWidth;
      }
    }
  }, [providerNotes, isSpeechRecognizing]);
  const [newProviderNote, setNewProviderNote] = useState("");

  const debounceDispatch = useCallback(
    useDebounce((value) => {
      dispatch(
        onChangeProviderNotes({
          note: value,
          notesTouched: true,
        })
      );
    }, 500),
    []
  );

  const handleInputChange = useCallback(
    (updatedProviderNote) => {
      setNewProviderNote(updatedProviderNote);
      if (!providerNotesTouched) dispatch(setProviderNotesTouched(true));
      debounceDispatch(updatedProviderNote);
    },
    [debounceDispatch, providerNotesTouched, dispatch]
  );

  useEffect(() => {
    setNewProviderNote(providerNotes);
  }, [providerNotes]);

  useEffect(() => {
    providerNotesRef.current = providerNotes || "";
  }, [providerNotes]);

  const handleTranscript = useCallback(
    (transcript) => {
      const currentNotes = providerNotesRef.current;
      const updated = currentNotes
        ? `${currentNotes} ${transcript}`
        : transcript;

      setNewProviderNote(updated);
      providerNotesRef.current = updated;

      dispatch(
        onChangeProviderNotes({
          note: updated,
          notesTouched: true,
        })
      );
    },
    [dispatch]
  );

  const { isTranscribing, startTranscription, stopTranscription } =
    useTranscription({
      onStart: () => dispatch(onSpeechRecognizing(true)),
      onStop: () => dispatch(onSpeechRecognizing(false)),
      onError: (message) => showErrorToast(message),
      onTranscript: handleTranscript,
    });

  const toggleSpeechRecognition = useCallback(() => {
    if (isTranscribing) {
      stopTranscription();
      return;
    }

    startTranscription();
  }, [isTranscribing, startTranscription, stopTranscription]);

  const [saveProviderNote] = useSaveProviderNoteMutation();

  const onSaveProviderNotes = async (
    isProcessAction = false,
    isSignAction = false,
    processState = "process"
  ) => {
    const submitAction = isProcessAction
      ? isSignAction
        ? processState === "process"
          ? SUBMIT_ACTIONS.SAVE_SIGN_AND_PROCESS
          : SUBMIT_ACTIONS.SAVE_SIGN_AND_UNPROCESS
        : SUBMIT_ACTIONS.SAVE_AND_PROCESS
      : SUBMIT_ACTIONS.SAVE;

    dispatch(
      toggleSubmitting({ type: "providerNotes", value: true, submitAction })
    );
    try {
      const response = await saveProviderNote({
        reportId: data._id,
        notes: newProviderNote,
      }).unwrap();

      if (!response.error) {
        dispatch(toggleSubmitting({ type: "providerNotes", value: false }));
        if (!isProcessAction) showSuccessToast(response?.message);

        const providerNote = response?.data?.provider_note;

        dispatch(
          selectedReport({
            id: data?._id,
            isLegacyReport: isLegacyReport,
            reportDetail: {
              ...reportDetail,
              provider_note: providerNote,
              alert_flag: alertStatus,
              warning_flag: attentionStatus,
            },
          })
        );

        dispatch(
          onChangeProviderNotes({
            note: providerNote?.note || "",
            notesTouched: false,
            isOriginal: true,
          })
        );

        if (isSignAction) {
          await signReports([data._id]);
        }

        if (isProcessAction) {
          await handleProcess(processState, isSignAction, false);
        }
      }
    } catch (error) {
      showErrorToast(error?.message || "An error occured, please try later.");
    } finally {
      dispatch(
        toggleSubmitting({ type: "providerNotes", value: false, submitAction })
      );
      await stopTranscription();
    }
  };

  const cancelProviderNotes = useCallback(() => {
    // Drop a keystroke still waiting in the debounce, or it lands after Cancel and re-applies the edit.
    debounceDispatch.cancel();
    setNewProviderNote(originalProviderNotes);
    dispatch(
      onChangeProviderNotes({
        note: originalProviderNotes,
        notesTouched: false,
        isOriginal: true,
      })
    );

    stopTranscription();
  }, [dispatch, debounceDispatch, originalProviderNotes, stopTranscription]);

  return {
    theme,
    dispatch,
    reportFIN,
    providerNotes,
    reportCurrentTab,
    reportDetail,
    newProviderNote,
    handleInputChange,
    flushProviderNote: debounceDispatch.flush,
    speechRecognition: null,
    cancelProviderNotes,
    onSaveProviderNotes,
    isSpeechRecognizing,
    originalProviderNotes,
    providerNotesTouched,
    providerNotesExpanded,
    providerNoteSubmitting,
    providerNotesEsigDisabled,
    toggleSpeechRecognition,
    providerNoteSubmittingAndProcessing,
    providerNoteSubmittingAndSigningAndProcessing,
    providerNoteSubmittingAndSigningAndUnProcessing,
    accountsMapper,
  };
};

export default useProviderNotes;
